// Chat utility functions

export const extractJsonFromString = (text) => {
  if (!text || typeof text !== "string") {
    console.warn("Invalid input to extractJsonFromString:", text);
    return null;
  }

  try {
    try {
      return JSON.parse(text);
    } catch (e) {
      // Not valid JSON, continue with cleanup
    }

    const match = text.match(/\{[^]*\}/);
    if (match) {
      let jsonStr = match[0];

      // Pre-process: Remove any existing escaped characters
      jsonStr = jsonStr.replace(/\\([^"])/g, "$1");

      // Add quotes around unquoted keys
      jsonStr = jsonStr.replace(/([{,]\s*)([a-zA-Z0-9_]+):/g, '$1"$2":');

      // Handle special characters in text values
      jsonStr = jsonStr.replace(/:\s*"([^"]*)"(?=[,}])/g, (match, content) => {
        // Clean the content: remove existing escapes and then properly escape
        const cleaned = content
          .replace(/\\([^"])/g, "$1") // Remove any existing escapes except for quotes
          .replace(/"/g, '\\"') // Escape quotes
          .replace(/\\/g, "\\\\") // Escape backslashes
          .replace(/[\\x00-\\x1F]/g, ""); // Remove control characters
        return `:"${cleaned}"`;
      });

      try {
        return JSON.parse(jsonStr);
      } catch (innerError) {
        console.warn("JSON parsing warning after cleanup:", innerError);
        console.warn("Cleaned text:", jsonStr);
        return null;
      }
    }
  } catch (error) {
    console.warn("JSON parsing warning:", error);
    console.warn("Problem text:", text);
    return null;
  }
  return null;
};

export const renderSuggestion = (suggestion) => {
  if (typeof suggestion === "string") {
    return suggestion;
  }
  if (typeof suggestion === "object") {
    return (
      suggestion.text ||
      suggestion.suggestion ||
      suggestion.description ||
      JSON.stringify(suggestion)
    );
  }
  return String(suggestion);
};

export const hasMarkdown = (text) => {
  // Handle non-string inputs
  if (!text || typeof text !== "string") {
    return false;
  }

  // Check for common markdown syntax
  const markdownPatterns = [
    /[*_]{1,2}[^*_]+[*_]{1,2}/, // bold/italic
    /#{1,6}\s+/, // headers
    /`[^`]+`/, // inline code
    /```[\s\S]*?```/, // code blocks
    /\[([^\]]+)\]\(([^)]+)\)/, // links
    /^\s*[-*+]\s+/m, // lists
    /^\s*\d+\.\s+/m, // numbered lists
    /\|[^|]+\|/, // tables
    /^\s*>\s+/m, // blockquotes
    /\n\n/, // Multiple newlines (paragraph breaks)
    / {2}$/m, // Line endings with two spaces (line breaks)
  ];

  return markdownPatterns.some((pattern) => pattern.test(text));
};

// Is `offset` on a line that is already a complete table row? Those need no repair.
const _isTableRow = (whole, offset) => {
  const start = whole.lastIndexOf("\n", offset) + 1;
  let end = whole.indexOf("\n", offset);
  if (end === -1) {
    end = whole.length;
  }
  const line = whole.slice(start, end).trim();
  return line.startsWith("|") && line.endsWith("|");
};

const LIST_MARKER_ONLY = /^\s*(?:\d{1,3}[.)]|[-*+])$/;

export const processText = (text) => {
  // Handle non-string inputs
  if (!text || typeof text !== "string") {
    if (typeof text === "object") {
      // If it's an object, try to get the text content
      return String(text.text || text.content || JSON.stringify(text));
    }
    return String(text || "");
  }

  return (
    text
      // First, handle escaped newlines
      .replace(/\\\\n/g, "\n")
      .replace(/\\\\t/g, "\t")
      // Then handle raw newlines in text
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t")
      // Replace HTML breaks (which break markdown tables or show as text) with semicolons
      .replace(/<br\s*\/?>/gi, "; ")
      // Remove conversation artifacts (Human:/AI: prefixes from leaked prompts)
      .replace(/^(Human|AI):\s*/gim, "")
      .replace(/\n(Human|AI):\s*/gim, "\n")
      // Fix squished headers: e.g. "brakes. **Key Requirements:**" or "**Header**: "
      // A list item's own label ("2. **Mounting:**", "- **Pulse:**") stays on its marker's line.
      .replace(
        /([a-zA-Z0-9.:)*])\s+(\*\*[^*]+:\*\*\s*|\*\*[^*]+\*\*:\s*)/g,
        (match, lead, label, offset, whole) =>
          LIST_MARKER_ONLY.test(whole.slice(whole.lastIndexOf("\n", offset) + 1, offset + 1))
            ? match
            : `${lead}\n\n${label}`
      )
      // Fix squished lists with bold starts: e.g. "type) - **S5.3"
      .replace(/([a-zA-Z0-9.:)*])\s+(-\s+\*\*?[A-Za-z0-9])/g, "$1\n$2")
      // Fix squished lists with section numbers: e.g. "Evidence:** - S4. " or "p.) - S5.6.7 "
      // Forces \n\n to ensure it breaks out of any preceding tables
      .replace(/([a-zA-Z0-9.:)*])\s*(?:[-–—]\s*)?(S\d+(?:\.\d+)*\s+[A-Z])/g, "$1\n\n- $2")
      // Fix squished tables: "p.) - # Table I"
      .replace(/([^\n])\s+(-\s+#\s*Table)/gi, "$1\n\n$2")
      // Fix squished table headers that run into the table
      .replace(/(-\s+#\s*Table[^\n|]+?)\s+\|\s+\|/gi, "$1\n\n| |")
      .replace(/(-\s+#\s*Table[^\n|]+?)\s+\|\s+([A-Za-z])/gi, "$1\n\n| $2")
      // Isolate the table separator row
      .replace(/\|\s+(\|[-|\s]+\|)\s+\|/g, "|\n$1\n|")
      // Break squished table rows, but never a row that is already well-formed — an empty cell
      // is legal, and splitting there broke the header's column count and killed the table.
      .replace(/([^\s|][ \t]*)\|\s+\|/g, (match, lead, offset, whole) =>
        _isTableRow(whole, offset) ? match : `${lead}|\n|`
      )
      // Ensure proper spacing for lists
      .replace(/\n\*/g, "\n\n*")
      .replace(/\n-/g, "\n\n-")
      // A numbered step is a step, not a section. This used to promote any "N. Text:" line
      // to a heading, which filled a 22-section report's contents list with test procedure
      // steps, rendered their bold labels as literal asterisks, and gave each one a section
      // review marker — approval icons stranded in the middle of paragraphs.
      // Ensure proper spacing for remaining lists
      .replace(/\n(\d+)\./g, "\n\n$1.")
      // Space around headings, keeping the level they were written at: "\n#+" -> "\n\n#"
      // flattened every "##" and "###" to "#", so a sub-section became a top-level one.
      .replace(/\n(#{1,6})/g, "\n\n$1")
      // Demote headings glued to a list marker ("- # Table I…") to a bold list item,
      // so they render inline instead of as a giant heading with a stray bullet.
      .replace(/^(\s*[-*+]\s+)#{1,6}\s+(.*)$/gm, "$1**$2**")
      // Ensure paragraphs have proper spacing
      .replace(/\n\n+/g, "\n\n")
      .trim()
  );
};

export const generateSuggestions = (text, suggestionSets) => {
  if (!text) {
    return [];
  } // Return empty array if text is null/undefined

  const lowerText = text.toLowerCase();
  if (lowerText.includes("certification") || lowerText.includes("timeline")) {
    return suggestionSets.certification;
  } else if (lowerText.includes("regulation") || lowerText.includes("fmvss")) {
    return suggestionSets.regulations;
  } else if (lowerText.includes("test") || lowerText.includes("procedure")) {
    return suggestionSets.testing;
  }
  return suggestionSets.default;
};

export const parseMessageContent = (message) => {
  // Parse content if it's a string that looks like JSON
  let messageContent = message.content;
  if (typeof messageContent === "string" && messageContent.trim().startsWith("{")) {
    try {
      messageContent = JSON.parse(messageContent);
    } catch (e) {
      console.warn("Failed to parse message content as JSON:", e);
    }
  }

  // Parse metadata if it's a string that looks like JSON
  let messageMetadata = message.metadata;
  if (typeof messageMetadata === "string" && messageMetadata.trim().startsWith("{")) {
    try {
      messageMetadata = JSON.parse(messageMetadata);
    } catch (e) {
      console.warn("Failed to parse message metadata as JSON:", e);
    }
  }

  // Try to parse the message content if it's JSON
  return {
    ...message,
    metadata: messageMetadata, // Explicitly preserve parsed metadata (fixes used_hits access)
    isUser:
      message.role === "user" ||
      message.message_type === "user" ||
      message.message_type === "isUser" ||
      message.isUser,
    isAgent:
      message.role === "assistant" ||
      message.message_type === "assistant" ||
      message.message_type === "bot" ||
      message.message_type === "isAgent" ||
      message.isAgent,
    text: (() => {
      const raw =
        typeof messageContent === "string"
          ? messageContent
          : messageContent?.answer || messageContent?.text || message.text;
      return raw !== null && raw !== undefined ? String(raw) : "";
    })(),
    suggestions: messageMetadata?.suggestions || message.suggestions,
    sources: messageMetadata?.sources || message.sources,
    image_urls: messageMetadata?.image_urls || message.image_urls,
    jiraTasks: messageMetadata?.jiraTasks || message.jiraTasks,
  };
};

export const downloadChatHistory = (messages, filename = "chat-history") => {
  try {
    const chatContent = messages
      .map((msg) => {
        const messageType = msg.isUser || msg.message_type === "user" ? "User" : "Assistant";
        const messageText =
          msg.text ||
          (msg.content && typeof msg.content === "object" ? msg.content.text : msg.content) ||
          "";
        return `${messageType}: ${messageText}`;
      })
      .join("\n\n");

    const blob = new Blob([chatContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    console.error("Error downloading chat:", error);
    return false;
  }
};

export const extractUserMessages = (messages) => {
  return messages
    .filter((msg) => {
      // Check for user messages in different formats
      return (
        msg.message_type === "user" ||
        msg.isUser ||
        (msg.content && msg.content.isUser) ||
        (msg.metadata && msg.metadata.message_type === "user")
      );
    })
    .map((msg) => {
      // Handle different message formats
      if (msg.content && typeof msg.content === "object" && msg.content.text) {
        return msg.content.text;
      }
      if (msg.content && typeof msg.content === "string") {
        return msg.content;
      }
      if (msg.text && typeof msg.text === "string") {
        return msg.text;
      }
      if (msg.message && typeof msg.message === "string") {
        return msg.message;
      }
      return "";
    })
    .filter((msg) => msg.trim() !== "") // Remove empty messages
    .reverse(); // Reverse the array to show most recent first
};

export const getLastMessageSuggestions = (messages, suggestionSets) => {
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage) {
    return suggestionSets.default;
  } // Return default suggestions if no messages

  // If the message already has suggestions, use those
  if (lastMessage.metadata && lastMessage.metadata.suggestions) {
    return Array.isArray(lastMessage.metadata.suggestions)
      ? lastMessage.metadata.suggestions
      : typeof lastMessage.metadata.suggestions === "string"
        ? [lastMessage.metadata.suggestions]
        : Object.values(lastMessage.metadata.suggestions);
  }

  // Get the text content from the message
  let messageText = "";
  if (typeof lastMessage.text === "string") {
    // If text is a string, try to parse it as JSON first
    try {
      const extractedContent = extractJsonFromString(lastMessage.text);
      if (typeof extractedContent === "object" && extractedContent !== null) {
        // If it's JSON, use the text property if it exists
        messageText = extractedContent.text || lastMessage.text;
      } else {
        // If not JSON, use the original text
        messageText = lastMessage.text;
      }
    } catch (e) {
      // If parsing fails, use the original text
      messageText = lastMessage.text;
    }
  } else if (typeof lastMessage.text === "object" && lastMessage.text !== null) {
    // If text is already an object, use its text property or stringify it
    messageText = lastMessage.text.text || JSON.stringify(lastMessage.text);
  } else {
    // Fallback to empty string if text is undefined or null
    messageText = "";
  }

  // Generate suggestions based on the extracted text
  return generateSuggestions(messageText, suggestionSets);
};
